import { useMemo, useState } from 'react';
import DeleteIcon from '@mui/icons-material/Delete';
import { Avatar, Box, Button, IconButton, Popover, Skeleton, Typography } from '@mui/material';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import moment from 'moment';
import { useEventBus } from 'context/EventBusContext/EventBusContext';
import { EventType, ValuationNoteChangedPayload } from 'context/EventBusContext/EventBusContext.type';
import { sanitizeNoteHtml } from 'dashboard409a/components/ValuationsTable/common/AnalystNoteHtml.utilities';
import { useNoteHandler } from 'dashboard409a/components/ValuationsTable/common/useNoteHandler';
import { AttioNotesService } from 'dashboard409a/services/attio/AttioNotesService';
import { CreateNoteRequest } from 'dashboard409a/services/attio.types';
import { NOTE_CONTENT_SX } from './AnalystNotesHistoryPanel.styles';
import {
  AnalystNoteHistoryItem,
  AnalystNoteReferenceType,
  AnalystNotesHistoryPanelProps,
} from './AnalystNotesHistoryPanel.types';
import { getInitials } from './AnalystNotesHistoryPanel.utilities';
import RichTextNoteEditor from './RichTextNoteEditor';

const buildCreateRequest = (
  referenceType: AnalystNoteReferenceType,
  referenceId: string | number,
  content: string
): CreateNoteRequest => {
  if (referenceType === 'at') {
    return {
      content,
      reference_type: 'at',
      attio_valuation_id: referenceId as string,
    };
  }

  return {
    content,
    reference_type: 'fu',
    fulfillment: referenceId as number,
  };
};

const AnalystNotesHistoryPanel = ({
  referenceType,
  referenceId,
  isDisabled,
}: AnalystNotesHistoryPanelProps) => {
  const queryClient = useQueryClient();
  const notesService = useMemo(() => new AttioNotesService(), []);
  const { publish } = useEventBus();

  const historyQueryKey = useMemo(
    () => ['analyst-notes-history', referenceType, referenceId],
    [referenceType, referenceId]
  );

  const [open, setOpen] = useState(false);
  const [pendingDeleteNoteId, setPendingDeleteNoteId] = useState<number | null>(null);
  const [anchorEl, setAnchorEl] = useState<HTMLButtonElement | null>(null);

  const handleClick = (event: React.MouseEvent<HTMLButtonElement>, noteId: number) => {
    setAnchorEl(event.currentTarget);
    setPendingDeleteNoteId(noteId);
    setOpen(true);
  };

  const handleClose = () => {
    setOpen(false);
    setAnchorEl(null);
    setPendingDeleteNoteId(null);
  };

  const { data, isFetching, isError } = useQuery<AnalystNoteHistoryItem[]>({
    queryKey: historyQueryKey,
    enabled: referenceId !== undefined && referenceId !== null,
    retry: false,
    refetchOnWindowFocus: false,
    queryFn: () => notesService.getNotesHistoryForReference(referenceType, referenceId as string | number),
  });

  const notes = data ?? [];

  const [draft, setDraft] = useState('');

  const handleNoteDelete = async (noteId: number) => {
    await notesService.deleteNote(noteId);
    const updatedNotes = notes.filter(n => n.id !== noteId);
    queryClient.setQueryData(historyQueryKey, updatedNotes);
    if (referenceType === 'at' && referenceId) {
      const latestNote = updatedNotes[0] ?? null;
      publish(EventType.VALUATION_NOTE_CHANGED, {
        valuationId: referenceId as string,
        latestNote: latestNote ? { id: latestNote.id, content: latestNote.content } : null,
      } satisfies ValuationNoteChangedPayload);
    }
    handleClose();
  };

  const { isNoteLoading, handleNoteSave } = useNoteHandler({
    onSuccess: async (_id, _content, createdNote) => {
      setDraft('');
      await queryClient.invalidateQueries({ queryKey: historyQueryKey });
      if (referenceType === 'at' && referenceId && createdNote) {
        publish(EventType.VALUATION_NOTE_CHANGED, {
          valuationId: referenceId as string,
          latestNote: { id: createdNote.id ?? 0, content: createdNote.content ?? '' },
        } satisfies ValuationNoteChangedPayload);
      }
    },
  });

  const canCreate = Boolean(referenceId) && !isDisabled && !isNoteLoading && draft.trim().length > 0;

  const initialsSourceFor = (note: AnalystNoteHistoryItem) => note.author_name || note.author_email || '';

  const handleSaveDraft = async () => {
    if (!referenceId || !canCreate) {
      return;
    }

    const request = buildCreateRequest(referenceType, referenceId, draft.trim());
    await handleNoteSave(referenceId, draft.trim(), undefined, request, false, undefined);
  };

  return (
    <Box sx={{ width: '100%', display: 'flex', flexDirection: 'column', gap: 1.5 }}>
      <Box sx={{ overflowY: 'auto', maxHeight: 240, borderRadius: 1 }}>
        {isFetching && (
          <Box sx={{ width: '100%' }}>
            <Skeleton variant="text" width="55%" height={18} />
            <Skeleton variant="rounded" width="100%" height={70} sx={{ mt: 1 }} />
            <Skeleton variant="rounded" width="100%" height={70} sx={{ mt: 1 }} />
          </Box>
        )}

        {!isFetching && isError && (
          <Typography variant="body2" color="error">
            Failed to load notes.
          </Typography>
        )}

        {!isFetching && !isError && notes.length === 0 && (
          <Typography variant="body2" color="text.secondary">
            No notes yet.
          </Typography>
        )}

        {!isFetching &&
          !isError &&
          notes.map(note => {
            const createdAtLabel = note.created_at
              ? moment(note.created_at).format('MMM D, YYYY h:mm A')
              : '';

            return (
              <Box
                key={note.id}
                sx={{
                  py: 1.25,
                  px: 1,
                  borderBottom: '1px solid',
                  borderColor: 'divider',
                  display: 'flex',
                  alignItems: 'flex-start',
                  gap: 1.5,
                }}
              >
                <Box sx={{ flex: 1, minWidth: 0 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Avatar
                      src={note.author_picture_url ?? undefined}
                      sx={{ width: 28, height: 28 }}
                    >
                      {getInitials(initialsSourceFor(note))}
                    </Avatar>

                    <Box sx={{ display: 'flex', flexDirection: 'column' }}>
                      <Typography variant="body2" sx={{ fontWeight: 600 }}>
                        {note.author_name || note.author_email}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        {createdAtLabel}
                      </Typography>
                    </Box>
                  </Box>

                  <Box
                    sx={NOTE_CONTENT_SX}
                    dangerouslySetInnerHTML={{ __html: sanitizeNoteHtml(note.content || '') }}
                  />
                </Box>

                <Box sx={{ display: 'flex', justifyContent: 'flex-end', pt: 0.5 }}>
                  <IconButton size="small" onClick={e => handleClick(e, note.id)}>
                    <DeleteIcon fontSize="small" />
                  </IconButton>
                  <Popover
                    open={open && pendingDeleteNoteId === note.id}
                    anchorEl={anchorEl}
                    onClose={handleClose}
                    anchorOrigin={{
                      vertical: 'bottom',
                      horizontal: 'left',
                    }}
                  >
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1, p: 2 }}>
                      <Typography variant="body2">Are you sure you want to delete this note?</Typography>
                      <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1 }}>
                        <Button size="small" onClick={() => handleNoteDelete(note.id)}>Delete</Button>
                        <Button size="small" onClick={handleClose}>Cancel</Button>
                      </Box>
                    </Box>
                  </Popover>
                </Box>
              </Box>
            );
          })}
      </Box>

      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
        <RichTextNoteEditor
          content={draft}
          onChange={setDraft}
          isDisabled={isDisabled}
          placeholder="Add note"
        />

        <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1 }}>
          <Button
            variant="contained"
            size="small"
            disabled={!canCreate}
            onClick={async () => {
              await handleSaveDraft();
            }}
          >
            Save
          </Button>
        </Box>
      </Box>
    </Box>
  );
};

export default AnalystNotesHistoryPanel;

